import { type Dispatch, type SetStateAction } from "react";

import { getLocationTypeFromLocation } from "src/lib/locationTree.helper";
import { type LocationModel } from "src/types/location";
import LocationType from "src/types/LocationType";

export interface LocationSearchProps {
  selectedLocation: LocationModel | null;
  locationOptions: LocationModel[];
  handleSelectedLocation: (location: LocationModel, locationType: LocationType, isRegion?: boolean) => void;
}

export interface LocationSelectionAutocompletePopupParams extends LocationSearchProps {
  anchorEl: HTMLElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
}

export interface AutocompleteOption {
  title: string;
  id: string;
  label: string;
  type: LocationType;
  isClickable: boolean;
  region: string;
  isVirtualRegion: boolean;
}

export interface RegionGroup {
  type: LocationType;
  region: AutocompleteOption;
  children: AutocompleteOption[];
}

function getChildTypesByParentId(locations: LocationModel[]): Map<string, Set<LocationType>> {
  const childTypesByParentId = new Map<string, Set<LocationType>>();
  for (const location of locations) {
    if (!location.ParentId) continue;
    const types = childTypesByParentId.get(location.ParentId) ?? new Set<LocationType>();
    types.add(getLocationTypeFromLocation(location));
    childTypesByParentId.set(location.ParentId, types);
  }
  return childTypesByParentId;
}

function getRegionsByLocationType(
  parentLocations: LocationModel[],
  childTypesByParentId: Map<string, Set<LocationType>>,
  locationType: LocationType
): LocationModel[] {
  return parentLocations.filter((location) => childTypesByParentId.get(location.Id)?.has(locationType) ?? false);
}

export function groupLocationOptions(locationOptions: LocationModel[]): RegionGroup[] {
  const locationMap = new Map(locationOptions.map((loc) => [loc.Id, loc]));
  const childTypesByParentId = getChildTypesByParentId(locationOptions);

  const allRegions = getRegionsByLocationType(locationOptions, childTypesByParentId, LocationType.Mall)
    .concat(getRegionsByLocationType(locationOptions, childTypesByParentId, LocationType.Shop))
    .concat(getRegionsByLocationType(locationOptions, childTypesByParentId, LocationType.Station));

  const regionIds = new Set(allRegions.map((region) => region.Id));

  const filtered = locationOptions
    .filter((loc) => loc.Type === "Shop" || loc.Type === "Mall" || loc.Type === "Station")
    .map((loc) => {
      const parent = loc.ParentId ? locationMap.get(loc.ParentId) : null;
      const locationType = LocationType[loc.Type as keyof typeof LocationType];

      if (parent && regionIds.has(parent.Id)) {
        return {
          id: loc.Id,
          label: loc.LocalName,
          type: locationType,
          isClickable: true,
          region: parent.LocalName.toUpperCase(),
          regionId: parent.Id,
          isVirtualRegion: false,
        };
      } else if (locationType === LocationType.Shop || locationType === LocationType.Mall || locationType === LocationType.Station) {
        return {
          id: loc.Id,
          label: loc.LocalName,
          type: locationType,
          isClickable: true,
          region: loc.LocalName.toUpperCase(),
          regionId: `virtual-${loc.Id}`,
          isVirtualRegion: true,
        };
      }

      return null;
    })
    .filter((option): option is NonNullable<typeof option> => option !== null);

  const groupedMap = new Map<LocationType, Map<string, RegionGroup>>();

  for (const option of filtered) {
    const { type, regionId, region, isVirtualRegion } = option;

    if (!groupedMap.has(type)) {
      groupedMap.set(type, new Map());
    }

    const typeGroup = groupedMap.get(type)!;

    if (!typeGroup.has(regionId)) {
      const regionLoc = locationMap.get(regionId);
      const label = regionLoc?.LocalName ?? region;

      typeGroup.set(regionId, {
        type,
        region: {
          title: "",
          id: regionId,
          label,
          type: regionLoc ? LocationType[regionLoc.Type as keyof typeof LocationType] : type,
          isClickable: true,
          region: label.toUpperCase(),
          isVirtualRegion,
        },
        children: [],
      });
    }

    typeGroup.get(regionId)!.children.push({
      title: "",
      id: option.id,
      label: option.label,
      type: option.type,
      isClickable: true,
      region: option.region,
      isVirtualRegion: option.isVirtualRegion,
    });
  }

  const result: RegionGroup[] = [];

  for (const typeGroup of groupedMap.values()) {
    const sortedGroups = Array.from(typeGroup.values()).sort((a, b) => {
      if (a.region.isVirtualRegion && !b.region.isVirtualRegion) return -1;
      if (!a.region.isVirtualRegion && b.region.isVirtualRegion) return 1;

      return a.region.label.localeCompare(b.region.label);
    });

    for (const group of sortedGroups) {
      group.children.sort((a, b) => a.label.localeCompare(b.label));
      result.push(group);
    }
  }

  return result;
}

export function filterOptions(options: AutocompleteOption[], state: { inputValue: string }): AutocompleteOption[] {
  const input = state.inputValue.trim().toLowerCase();
  if (!input) return options;

  const matchedRegions = options.filter((opt) => opt.title?.startsWith("region-") && opt.label.toLowerCase().includes(input));

  const matchedChildren = options.filter((opt) => !opt.title?.startsWith("region-") && opt.label.toLowerCase().includes(input));

  if (matchedRegions.length > 0 && matchedChildren.length === 0) {
    return matchedRegions;
  }

  const matchedRegionIds = new Set<string>();
  matchedChildren.forEach((child) => {
    if (child.region) matchedRegionIds.add(child.region);
  });

  const parentRegions = options.filter((opt) => opt.title?.startsWith("region-") && matchedRegionIds.has(opt.region));

  const result: AutocompleteOption[] = [];
  matchedChildren.forEach((child) => {
    const region = parentRegions.find((r) => r.region === child.region);
    if (region && !result.includes(region)) result.push(region);
    result.push(child);
  });

  return result;
}
